import { GroupMemberWithWorkdays, useAppSelector } from "@lib/store";
import { Avatar } from "@molecules";
import { cn, getWorkdayStatusIconName } from "@utils";
import { Icon } from "@atoms";
import { Tooltip } from "react-tooltip";
import { OfficeWorkday } from "@lib/store/src/data/timeslots/apiTypes";
import { useTranslation } from "react-i18next";
import { PropsWithChildren } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { extractWorkdayStatus, getOfficeLabel } from "../../utils";
import { useConnections } from "@pages";
import { useConnectBasePath } from "@hooks";

export const ScheduleItem = ({ groupMemberWithWorkdays }: { groupMemberWithWorkdays: GroupMemberWithWorkdays }) => {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const { daysForSelectedWeek, compactView, canShowFullContent } = useConnections();

  const currentUserId = useAppSelector(state => state.data.user.user.id);

  const navigate = useNavigate();
  const connectBasePath = useConnectBasePath();

  const canNavigateToMemberDetail = true; //groupMemberWithWorkdays.user.id !== currentUserId;

  return (
    <>
      <WrapperComponent
        onClick={
          canNavigateToMemberDetail
            ? () => navigate(`${connectBasePath}/groups/${groupId}/members/${groupMemberWithWorkdays.user.id}`)
            : undefined
        }
        className="grid grid-cols-subgrid col-span-full items-center py-4 md:py-8 justify-between -mx-4 md:-mx-8 px-4 md:px-8">
        <div className={cn("pb-2 md:p-0 flex items-center gap-3", { "col-span-full": compactView })}>
          {!compactView && (
            <Avatar
              className="flex-shrink-0"
              user={groupMemberWithWorkdays.user}
              size="medium"
            />
          )}

          <h4 className="truncate text-sm md:text-[15px]">
            {groupMemberWithWorkdays.user.name}{" "}
            <span className="text-mapiq-black-500">
              {groupMemberWithWorkdays.user.id === currentUserId ? `(${t("You")})` : ""}
            </span>
          </h4>
        </div>
        {daysForSelectedWeek.map((day, index) => {
          const workdaysForDay = groupMemberWithWorkdays.workdays.filter(wd => wd.localStart.startsWith(day));

          const officeDay = workdaysForDay.find((wd): wd is OfficeWorkday => wd.status === "OfficeDay");

          const workdayStatus = extractWorkdayStatus(workdaysForDay);

          const highlight = workdayStatus === "OfficeDay";
          const officeLabel = getOfficeLabel(workdayStatus, officeDay, t);

          return (
            <div
              key={index}
              className="grid md:grid-cols-[auto_1fr] justify-center items-center gap-2">
              <Tooltip
                id={`tooltip-${day}-${groupMemberWithWorkdays.user.id}`}
                clickable={true}
                hidden={!canShowFullContent}
                className="bg-mapiq-black-500"
                noArrow
                place="bottom-start"
                delayShow={250}>
                <p>{officeLabel}</p>
              </Tooltip>
              <div
                data-tooltip-id={`tooltip-${day}-${groupMemberWithWorkdays.user.id}`}
                className={cn("size-7 md:size-10 rounded-full flex justify-center items-center transition-colors", {
                  "bg-energizing-yellow-500* text-mapiq-black-800* border-transparent": highlight,
                  "border bg-white text-mapiq-black-500": !highlight,
                })}>
                <Icon
                  icon={getWorkdayStatusIconName(workdayStatus)}
                  color="currentColor"
                  className="size-4 md:size-5"
                />
              </div>

              {!canShowFullContent && <p className="line-clamp-2 leading-tight text-mapiq-black-500">{officeLabel}</p>}
            </div>
          );
        })}
      </WrapperComponent>
      <hr className="col-span-full -mx-4 md:-mx-8" />
    </>
  );
};

const WrapperComponent = ({
  className,
  onClick,
  children,
}: PropsWithChildren<{ className?: string; onClick?: () => void }>) => {
  return onClick ? (
    <button
      onClick={onClick}
      className={`${className} hover:bg-mapiq-black-800*/[0.03] transition-colors`}>
      {children}
    </button>
  ) : (
    <div className={className}>{children}</div>
  );
};
